import React, { useState, useRef } from 'react';
import { SimulationTemplate, SimulationStep, ResultBlock, GraphContainer, ResultMessage } from '../SimulationTemplate';
import { Text } from '../../ui/typography/Text';
import { AccentButton } from '../../ui/buttons/AccentButton';
import { Input } from '../../ui/Input';
import { OutlineButton } from '../../ui/buttons/OutlineButton';
import { Brain, Microscope, BookOpen, BarChart3 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, Label } from 'recharts';

const letters = [
  { 
    letter: 'R',
    actualRatio: 0.42,
    description: 'Think of a word containing the letter R.'
  }
];

type SimulationStage = 'intro' | 'word-generation' | 'position-judgment' | 'ratio-estimation' | 'results';

// Custom tooltip component for better styling
const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const value = payload[0].value;
    const name = payload[0].payload.name;
    const isUserEstimate = name === 'Your Estimate';
    const compareText = value < 1 
      ? 'Third position is more common' 
      : 'First position is more common';
      
    return (
      <div className="bg-white shadow-lg rounded-md p-4 border border-gray-200">
        <p className="font-medium text-gray-800 mb-1">{name}</p>
        <p className="text-sm text-gray-700">
          Ratio: <span className="font-medium text-blue-600">{Number(value).toFixed(2)}:1</span>
        </p>
        <p className="text-xs text-gray-500 mt-1">
          {compareText}
        </p>
      </div>
    );
  }
  
  return null;
};

const AvailabilityHeuristicSimulation: React.FC = () => {
  // ========== STATE MANAGEMENT ==========
  const [started, setStarted] = useState(false);
  const [stage, setStage] = useState<SimulationStage>('intro');
  const [words, setWords] = useState<{ [key: string]: string }>({});
  const [positions, setPositions] = useState<{ [key: string]: 'first' | 'third' }>({});
  const [ratios, setRatios] = useState<{ [key: string]: string }>({});
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [showResults, setShowResults] = useState(false);
  
  // Reference for scrolling
  const simulationRef = useRef<HTMLDivElement>(null);

  // ========== EVENT HANDLERS ==========
  const handleWordChange = (letter: string, value: string) => {
    setErrors(prev => ({ ...prev, [letter]: '' }));
    setWords(prev => ({ ...prev, [letter]: value }));
  };

  const handlePositionSelect = (letter: string, position: 'first' | 'third') => {
    setPositions(prev => ({ ...prev, [letter]: position }));
    setErrors(prev => ({ ...prev, [letter]: '' }));
  };

  const handleRatioChange = (letter: string, value: string) => {
    setErrors(prev => ({ ...prev, [`${letter}_ratio`]: '' }));
    // Allow digits, decimal point, and backspace
    const numValue = value.replace(/[^\d.]/g, '');
    // Ensure only one decimal point
    const decimalCount = (numValue.match(/\./g) || []).length;
    if (decimalCount <= 1) {
      const floatValue = parseFloat(numValue);
      if (isNaN(floatValue) || floatValue <= 99) {
        setRatios(prev => ({ ...prev, [letter]: numValue }));
      }
    }
  };

  const validateWords = () => {
    const newErrors: { [key: string]: string } = {};
    let hasErrors = false;

    letters.forEach(({ letter }) => {
      if (!words[letter]?.trim()) {
        newErrors[letter] = 'Please enter a word';
        hasErrors = true;
      }
    });

    setErrors(newErrors);
    return !hasErrors;
  };

  const validatePositions = () => {
    const newErrors: { [key: string]: string } = {};
    let hasErrors = false;

    letters.forEach(({ letter }) => {
      if (!positions[letter]) {
        newErrors[letter] = 'Please select which position is more common';
        hasErrors = true;
      }
    });

    setErrors(newErrors);
    return !hasErrors;
  };

  const validateRatios = () => {
    const newErrors: { [key: string]: string } = {};
    let hasErrors = false;

    letters.forEach(({ letter }) => {
      if (!ratios[letter]) {
        newErrors[`${letter}_ratio`] = 'Please enter a ratio';
        hasErrors = true;
      } else {
        const value = parseFloat(ratios[letter]);
        if (isNaN(value) || value <= 0) {
          newErrors[`${letter}_ratio`] = 'Please enter a number greater than 0';
          hasErrors = true;
        }
      }
    });

    setErrors(newErrors);
    return !hasErrors;
  };

  const handleNext = () => {
    switch (stage) {
      case 'intro':
        setStage('word-generation');
        break;
      case 'word-generation':
        if (validateWords()) {
          setStage('position-judgment');
        }
        break;
      case 'position-judgment':
        if (validatePositions()) {
          setStage('ratio-estimation');
        }
        break;
      case 'ratio-estimation':
        if (validateRatios()) {
          setStage('results');
          setShowResults(true);
        }
        break;
    }
  };

  // Reset the simulation
  const handleReset = () => {
    setStarted(false);
    setStage('word-generation');
    setWords({});
    setPositions({});
    setRatios({});
    setErrors({});
    setShowResults(false);
    simulationRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getRatioText = (letter: string) => {
    const position = positions[letter];
    const ratio = parseFloat(ratios[letter]) || 0;
    if (!position || !ratio) return '';
    // Always express ratio as first:third
    const firstToThirdRatio = position === 'first' ? ratio : 1/ratio;
    return `${firstToThirdRatio.toFixed(2)}:1 first to third position ratio`;
  };

  const renderBar = (value: number, maxValue: number, color: string, label: string) => (
    <div className="flex items-center gap-3">
      <Text size="sm" className="text-gray-600 w-24">
        {label}:
      </Text>
      <div className="h-6 flex-1 bg-gray-100 rounded-full overflow-hidden">
        <div 
          className={`h-full ${color} transition-all duration-500`}
          style={{ width: `${(value / maxValue) * 100}%` }}
        />
      </div>
      <Text size="sm" className="text-gray-600 w-16">
        {value.toFixed(2)}:1
      </Text>
    </div>
  );

  // ========== SIMULATION-SPECIFIC DATA ==========
  const simulationData = {
    title: "The Availability Heuristic",
    description: "Experience how our minds judge frequency based on how easily examples come to mind, recreating a classic experiment from Tversky and Kahneman's 1973 study.",
    citation: "Tversky & Kahneman (1973), \"Availability: A Heuristic for Judging Frequency and Probability,\" Cognitive Psychology"
  };

  // ========== COMPONENT CONTENT ==========
  const simulationContent = (
    <div ref={simulationRef} className="space-y-8">
      {/* TASK 1: Word Generation */}
      <SimulationStep 
        title="Step 1: Word Generation" 
        isActive={stage === 'word-generation'}
      >
        <div className="space-y-6">
          <Text className="text-gray-700">
            Think of a word that contains the letter R. Any position is fine.
          </Text>
          
          <div className="space-y-6">
            {letters.map(({ letter, description }) => (
              <div key={letter} className="space-y-2">
                <Text className="text-gray-700">
                  {description}
                </Text>
                <div className="max-w-[200px]">
                  <Input
                    type="text"
                    value={words[letter] || ''}
                    onChange={(e) => handleWordChange(letter, e.target.value)}
                    placeholder="Enter a word"
                    className={errors[letter] ? 'border-red-300' : ''}
                    disabled={stage !== 'word-generation'}
                  />
                  {errors[letter] && (
                    <Text size="sm" className="text-red-500 mt-1">
                      {errors[letter]}
                    </Text>
                  )}
                </div>
              </div>
            ))}
          </div>

          {stage === 'word-generation' && (
            <div className="flex justify-center mt-6">
              <AccentButton onClick={handleNext}>
                Next Step
              </AccentButton>
            </div>
          )}
        </div>
      </SimulationStep>

      {/* TASK 2: Position Judgment */}
      {stage !== 'word-generation' && (
        <SimulationStep 
          title="Step 2: Position Judgment" 
          isActive={stage === 'position-judgment'}
        >
          <div className="space-y-6">
            <Text className="text-gray-700">
              Is the letter R more common in the first or third position of English words?
            </Text>
            
            <div className="space-y-8">
              {letters.map(({ letter }) => (
                <div key={letter} className="space-y-4">
                  <Text className="text-gray-700">
                    Your word: "{words[letter]}"
                  </Text>
                  <div className="flex gap-4">
                    <OutlineButton
                      onClick={() => handlePositionSelect(letter, 'first')}
                      active={positions[letter] === 'first'}
                      disabled={stage !== 'position-judgment'}
                    >
                      As first letter
                    </OutlineButton>
                    <OutlineButton
                      onClick={() => handlePositionSelect(letter, 'third')}
                      active={positions[letter] === 'third'}
                      disabled={stage !== 'position-judgment'}
                    >
                      As third letter
                    </OutlineButton>
                  </div>
                  {errors[letter] && (
                    <Text size="sm" className="text-red-500">
                      {errors[letter]}
                    </Text>
                  )}
                </div>
              ))}
            </div>

            {stage === 'position-judgment' && (
              <div className="flex justify-center mt-6">
                <AccentButton onClick={handleNext}>
                  Next Step
                </AccentButton>
              </div>
            )}
          </div>
        </SimulationStep>
      )}

      {/* TASK 3: Ratio Estimation */}
      {stage !== 'word-generation' && stage !== 'position-judgment' && (
        <SimulationStep 
          title="Step 3: Ratio Estimation" 
          isActive={stage === 'ratio-estimation'}
        >
          <div className="space-y-6">
            <Text className="text-gray-700">
              How many times more common do you think your chosen position is?
            </Text>
            
            <div className="space-y-8">
              {letters.map(({ letter }) => {
                const position = positions[letter];
                const ratio = parseFloat(ratios[letter]) || 0;
                const userPosition = position;
                const userInputRatio = ratio;
                const userEstimatedRatio = userPosition === 'first' ? userInputRatio : 1/userInputRatio;
                const maxRatio = Math.max(userEstimatedRatio, 2) * 1.2;
                const userCorrectDirection = userEstimatedRatio < 1;
                
                return (
                  <div key={letter} className="space-y-4">
                    <Text className="text-gray-700">
                      Your word: "{words[letter]}"
                    </Text>
                    <Text className="text-gray-700">
                      You selected: {position === 'first' ? 'First position' : 'Third position'}
                    </Text>
                    <Text className="text-gray-700">
                      How many times more common is the {position} position?
                    </Text>
                    <div className="max-w-[200px] flex items-center gap-2">
                      <Input
                        type="text"
                        value={ratios[letter] || ''}
                        onChange={(e) => handleRatioChange(letter, e.target.value)}
                        placeholder="Enter ratio"
                        className={errors[`${letter}_ratio`] ? 'border-red-300' : ''}
                        disabled={stage !== 'ratio-estimation'}
                      />
                      <Text size="sm" className="text-gray-600 whitespace-nowrap">
                        to 1
                      </Text>
                    </div>
                    {errors[`${letter}_ratio`] && (
                      <Text size="sm" className="text-red-500">
                        {errors[`${letter}_ratio`]}
                      </Text>
                    )}
                    {position && ratios[letter] && (
                      <Text size="sm" className="text-gray-600 italic">
                        {getRatioText(letter)}
                      </Text>
                    )}
                  </div>
                );
              })}
            </div>

            {stage === 'ratio-estimation' && (
              <div className="flex justify-center mt-6">
                <AccentButton onClick={handleNext}>
                  See Results
                </AccentButton>
              </div>
            )}
          </div>
        </SimulationStep>
      )}

      {/* RESULTS SECTION */}
      {showResults && (
        <div className="space-y-6">
          {/* Understanding Results Block */}
          <ResultBlock
            title="Understanding the Results"
            icon={Brain}
            variant="highlight"
          >
            <div className="space-y-8">
              {/* Step 1: User Results */}
              <div className="space-y-5">
                <Text className="text-gray-800 font-medium text-lg">
                  Your Results
                </Text>
                
                <Text className="text-gray-700">
                  You chose <span className="font-medium text-orange-600">
                    {positions['R'] === 'first' ? 'first position' : 'third position'}
                  </span> as more common for the letter R, with an estimated ratio of{' '}
                  <span className="font-medium text-orange-600">{getRatioText('R')}</span>.
                </Text>
                
                {/* Visualization - Ultra Clean */}
                {letters.map(({ letter, actualRatio }) => {
                  const userPosition = positions[letter];
                  const userInputRatio = parseFloat(ratios[letter]) || 0;
                  const userEstimatedRatio = userPosition === 'first' ? userInputRatio : 1/userInputRatio;
                  const maxRatio = Math.max(userEstimatedRatio, actualRatio, 2) * 1.2;
                  const userCorrectDirection = userEstimatedRatio < 1;
                  
                  return (
                    <div key={letter} className="space-y-4">
                      <div className="w-full overflow-hidden" style={{ height: '400px' }}>
                        <GraphContainer>
                          <div className="w-full h-[350px] md:h-[400px] min-h-[300px]" style={{ minHeight: '300px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                              <BarChart 
                                data={[
                                  { name: 'Your Estimate', value: userEstimatedRatio },
                                  { name: 'Actual Ratio', value: actualRatio }
                                ]}
                                layout="vertical"
                                margin={{ top: 20, right: 60, left: 20, bottom: 30 }}
                              >
                                <CartesianGrid strokeDasharray="4 4" stroke="#e5e7eb" horizontal={true} vertical={true} />
                                <XAxis 
                                  type="number" 
                                  domain={[0, maxRatio]} 
                                  tick={{ fontSize: 12 }}
                                  tickLine={{ stroke: '#9ca3af' }}
                                  axisLine={{ stroke: '#9ca3af' }}
                                >
                                  <Label 
                                    value="First:Third Position Ratio" 
                                    position="insideBottom" 
                                    offset={-10} 
                                    style={{ fontSize: '12px', fill: '#6b7280' }}
                                  />
                                </XAxis>
                                <YAxis 
                                  dataKey="name" 
                                  type="category" 
                                  tick={{ fontSize: 12 }}
                                  tickLine={{ stroke: '#9ca3af' }}
                                  axisLine={{ stroke: '#9ca3af' }}
                                />
                                {/* Custom tooltip with better styling */}
                                <Tooltip 
                                  content={<CustomTooltip />}
                                  cursor={{ fill: 'rgba(224, 231, 255, 0.2)' }}
                                  wrapperStyle={{ outline: 'none', zIndex: 100 }}
                                  wrapperClassName="hidden sm:block" /* Hide on mobile, show on tablet/desktop */
                                />
                                <Bar 
                                  dataKey="value" 
                                  name="First:Third Ratio" 
                                  barSize={40}
                                  // Add labels that are visible on mobile
                                  label={{
                                    position: 'right',
                                    formatter: (value: number) => `${Number(value).toFixed(2)}:1`,
                                    fontSize: 12,
                                    fill: '#4b5563'
                                  }}
                                  animationDuration={800}
                                >
                                  {[
                                    { dataKey: 'value', fill: '#3b82f6', stackId: 'a', index: 0 }, 
                                    { dataKey: 'value', fill: '#10b981', stackId: 'a', index: 1 }
                                  ].map((entry, index) => (
                                    <Cell 
                                      key={index} 
                                      fill={index === 0 ? '#3b82f6' : '#10b981'} 
                                      cursor="pointer"
                                      style={{ filter: 'drop-shadow(0px 2px 2px rgba(0, 0, 0, 0.1))' }}
                                    />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </GraphContainer>
                      </div>
                      
                      <Text className="text-gray-500 text-sm">
                        Note: Ratio less than 1 indicates third position is more common
                      </Text>
                      
                      {/* Using ResultMessage for feedback */}
                      <ResultMessage 
                        isCorrect={userCorrectDirection}
                        message={userCorrectDirection 
                          ? 'You correctly identified that the letter R appears more frequently in the third position. This shows you were able to overcome the cognitive bias in this case.'
                          : 'You showed the same pattern as most participants in the original study, demonstrating how even when trying to be objective, our judgments are influenced by how easily examples come to mind.'}
                      />
                    </div>
                  );
                })}
              </div>

              {/* Step 2: Connection to the Bias */}
              <div className="space-y-3">
                <Text className="text-gray-800 font-medium text-lg">
                  How This Relates to the Availability Heuristic
                </Text>
                <Text className="text-gray-700">
                  This experiment demonstrates the availability heuristic in action. We naturally organize words in our memory 
                  by their first letter, making it easier to retrieve examples of words that start with a specific letter. 
                  This mental availability leads us to overestimate how frequently letters appear in the first position of words.
                </Text>
              </div>
              
              {/* Step 3: Study Findings */}
              <div className="space-y-3">
                <Text className="text-gray-800 font-medium text-lg">
                  What the Original Study Found
                </Text>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>Most participants (about 2/3) incorrectly judged the first position to be more common</li>
                  <li>They typically estimated a 2:1 ratio in favor of first position</li>
                  <li>In reality, for consonants like R, K, L, N, and V, the third position is more common</li>
                  <li>For the letter R specifically, it appears more than twice as often in the third position (ratio of 0.42:1)</li>
                </ul>
              </div>
            </div>
          </ResultBlock>

          {/* About the Study Block */}
          <ResultBlock
            title="About the Study"
            icon={BookOpen}
            variant="muted"
          >
            <div className="space-y-4">
              <Text className="text-gray-700">
                Based on research by {simulationData.citation}. This classic study explored how the ease with which 
                examples come to mind affects our judgments of frequency and probability.
              </Text>
              
              <Text className="text-sm text-gray-500 italic">
                This simulation is designed for educational purposes. Individual results may vary.
              </Text>
            </div>
          </ResultBlock>

          {/* Try Again Button */}
          <div className="flex justify-center">
            <AccentButton onClick={handleReset}>
              Try Again
            </AccentButton>
          </div>
        </div>
      )}
    </div>
  );

  // ========== MAIN COMPONENT RENDER ==========
  return (
    <SimulationTemplate
      title={simulationData.title}
      description={simulationData.description}
    >
      {!started ? (
        <div className="flex justify-center">
          <AccentButton onClick={() => {
            setStarted(true);
            setStage('word-generation');
          }}>
            Start Simulation
          </AccentButton>
        </div>
      ) : (
        simulationContent
      )}
    </SimulationTemplate>
  );
};

export default AvailabilityHeuristicSimulation; 